export type PermissionState = 'hidden' | 'disabled' | 'readonly' | 'editable';

export interface PermissionClaims {
  permissions: string[];
}

/** Architecture PERM codes only — never ROLE-* or raw role names. */
export const PERM_CODE_PATTERN = /^PERM-[a-z0-9.-]+$/i;

export function isPermCode(code: string): boolean {
  return PERM_CODE_PATTERN.test(code) && !code.startsWith('ROLE-');
}

export function assertPermCode(code: string): void {
  if (!isPermCode(code)) {
    throw new Error(`Invalid permission code "${code}" — use PERM-* catalogue codes only`);
  }
}

export function parsePermissionsFromToken(accessToken: string | null): PermissionClaims {
  if (!accessToken) return { permissions: [] };
  try {
    const payload = accessToken.split('.')[1];
    if (!payload) return { permissions: [] };
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as {
      permissions?: string[];
      perms?: string[];
    };
    const raw = decoded.permissions ?? decoded.perms ?? [];
    return { permissions: raw.filter(isPermCode) };
  } catch {
    return { permissions: [] };
  }
}

export function hasPermission(claims: PermissionClaims, perm: string): boolean {
  return claims.permissions.includes(perm);
}

function readPermFor(perm: string): string | null {
  if (perm.endsWith('.update')) return perm.replace(/\.update$/, '.read');
  if (perm.endsWith('.create')) return perm.replace(/\.create$/, '.read');
  if (perm.endsWith('.delete')) return perm.replace(/\.delete$/, '.read');
  if (perm.endsWith('.execute')) return perm.replace(/\.execute$/, '.read');
  return null;
}

export interface ResolvePermissionOptions {
  /** PERM codes temporarily blocked (present but disabled per 07-01). */
  disabled?: readonly string[];
}

export function resolvePermissionState(
  claims: PermissionClaims,
  perm: string | undefined,
  authenticated: boolean,
  options?: ResolvePermissionOptions,
): PermissionState {
  if (!perm) return 'editable';
  if (!isPermCode(perm)) return 'hidden';
  if (!authenticated) return 'hidden';
  if (options?.disabled?.includes(perm)) return 'disabled';
  if (hasPermission(claims, perm)) return 'editable';
  const readPerm = readPermFor(perm);
  if (readPerm && hasPermission(claims, readPerm)) return 'readonly';
  return 'hidden';
}
